import { CampaignProductResults } from "src/graphql/generated/graphql";

export interface RatingBarItem {
    label: string;
    value: number;
}

// Recommend-% → traffic-light colour. Mirrors the original OverviewTab logic.
export const getRecommendationColor = (wouldRecommendPercent: number | null | undefined): string => {
    if (wouldRecommendPercent === undefined) {
        return "grey";
    }

    if (wouldRecommendPercent >= 80) {
        return "#029218";
    }

    if (wouldRecommendPercent >= 50) {
        return "#fab453";
    }

    return "#ff001e";
};

// Traffic-light colour for a 1–5 star value: 5 green → 1 red. Accepts a continuous
// value too (e.g. an attribute mean out of 5), bucketed to the nearest band.
export const getStarColor = (stars: number): string => {
    if (stars >= 4.5) {return "#2F9E44";} // green
    if (stars >= 3.5) {return "#66BB6A";} // light green
    if (stars >= 2.5) {return "#FB8C00";} // orange
    if (stars >= 1.5) {return "#F4511E";} // deep orange
    return "#E03131"; // red
};

// Folds the stored 1–10 ratingBreakup into the 5-star distribution (5★ first).
export const foldRatingBreakup = (
    totalRatingBreakup: CampaignProductResults["totalRatingBreakup"]
): RatingBarItem[] => {
    const defaultRatingBreakup = new Array(5).fill(0).map((_, index) => ({
        label: `${index + 1} `,
        value: 0,
    }));

    const roundedRatings = totalRatingBreakup?.reduce(
        (acc, { rating, count }) => {
            const roundedRating = Math.max(1, Math.round(rating / 2));
            acc[roundedRating - 1].value += count;
            return acc;
        },
        [...defaultRatingBreakup]
    );

    return roundedRatings?.reverse() || [];
};

// NPS buckets from the raw 1–10 ratingBreakup (promoters ≥9, passives 7–8, detractors ≤6).
export const getNpsBuckets = (totalRatingBreakup: CampaignProductResults["totalRatingBreakup"]) => {
    const promoters = totalRatingBreakup?.reduce((acc, { rating, count }) => (rating >= 9 ? acc + count : acc), 0);
    const passives = totalRatingBreakup?.reduce(
        (acc, { rating, count }) => (rating >= 7 && rating <= 8 ? acc + count : acc),
        0
    );
    const detractors = totalRatingBreakup?.reduce((acc, { rating, count }) => (rating <= 6 ? acc + count : acc), 0);

    return { promoters, passives, detractors };
};
